import * as _ from 'lodash';
import { Button } from 'rbx';
import * as React from 'react';
import { useMutation } from '@apollo/client';
import { Navigate } from 'react-router-dom';

import { PLANT_SAVE_MUTATION } from './../../api/GraphQLQueries/Plant';
import Error from './../../components/layout/Error';
import Message from './../../components/layout/Message';
import Pane from './../../components/ui/Pane';
import IGroupModel from './../../models/IGroupModel';
import IPlantModel, { IPlantUpdateModel } from './../../models/IPlantModel';
import Routing from './../../Routing';
import omit from './../../utils/omit';
import { todo } from './../../utils/translate';
import PlantUpdateForm from './forms/PlantUpdateForm';
import PlantDetailQueryContainer from './queries/PlantDetailQuery';

interface IProps {
    idPlant: string;
    onCancel: () => void;
}

const PlantUpdate: React.FC<IProps> = ({ idPlant, onCancel }) => {
    const [plant, setPlant] = React.useState<IPlantUpdateModel | undefined>(undefined);
    const [error, setError] = React.useState(false);
    const [success, setSuccess] = React.useState(false);

    const [updatePlantMutation] = useMutation(PLANT_SAVE_MUTATION, {
        onError: () => {
            setError(true);
            setSuccess(false);
        },
        onCompleted: () => {
            setError(false);
            setSuccess(true);
            setPlant(undefined);
        },
    });

    const updateValues = (updatedPlant: IPlantUpdateModel) => {
        setPlant(updatedPlant);
        setError(false);
        setSuccess(false);
    };

    const formSubmit = (currentPlant: IPlantUpdateModel) => () => {
        const plantToSave = omit(plant || currentPlant, ['s3Folder']);
        updatePlantMutation({ variables: { plant: plantToSave } });
    };

    const getGroupIdsFromPlantGroups = (fetchedPlant: IPlantModel): number[] | undefined => {
        const { groups } = fetchedPlant;
        if (_.isEmpty(groups)) {
            return undefined;
        }
        return groups!.map((group: IGroupModel) => group.id);
    };

    const convertPlantToPlantUpdateModel = (fetchedPlant: IPlantModel): IPlantUpdateModel => {
        const plantUpdateOnly: IPlantUpdateModel = _.pick(fetchedPlant, [
            'id',
            'name',
            's3Folder',
            'customerDisplayName',
            'invoiceAddress',
            'deliveryAddress',
        ]);
        plantUpdateOnly.groupIds = getGroupIdsFromPlantGroups(fetchedPlant);
        return plantUpdateOnly;
    };

    const isPlantInValid = () => {
        return (
            !plant ||
            !plant.name ||
            plant.name.length < 3 ||
            _.some(
                [
                    ..._.values(_.omit(plant.invoiceAddress, ['latitude', 'longitude'])),
                    ..._.values(_.omit(plant.deliveryAddress, ['latitude', 'longitude'])),
                ],
                _.isEmpty,
            )
        );
    };

    const renderPlantUpdateForm = (plantData: IPlantUpdateModel) => {
        const updatePlantButton = (
            <Button
                onClick={() => {
                    if (!isPlantInValid()) {
                        formSubmit(plantData)();
                    }
                }}
                disabled={isPlantInValid()}
            >
                Save
            </Button>
        );

        return (
            <Pane
                onClose={onCancel}
                actionButtons={updatePlantButton}
                title={todo('Edit plant')}
                sidepane={true}
            >
                <PlantUpdateForm plant={plantData} onUpdate={updateValues} />
            </Pane>
        );
    };

    const renderPlantForm = (plantData: IPlantModel) => {
        if (!plantData) {
            return <Navigate to={Routing.PLANTS.route} />;
        }

        const currentPlant = plant || convertPlantToPlantUpdateModel(plantData);
        return renderPlantUpdateForm(currentPlant);
    };

    return (
        <>
            {error && <Error>Plant update error</Error>}
            {success && <Message type="success">Successfully updated plant</Message>}
            {idPlant && (
                <PlantDetailQueryContainer
                    id={idPlant}
                    renderFetchedPlantData={renderPlantForm}
                />
            )}
        </>
    );
};

export default PlantUpdate;
